/**
 * 한글 파일 이벤트 랜딩 v2 설정.
 *
 * 유형(exam/notice/record)별 라우트·카피·입력 필드·프리셋·예시를 한곳에 둔다.
 * 모든 필드는 칩으로만 채워지고 기본값이 있어 클릭 0번으로도 "만들기"가 가능해야 한다.
 * 텍스트 필드(topic/key_points/observations)는 예시 칩을 누르면 채워지고 직접 수정할 수 있다.
 * 시험지 topic 은 성취기준 칩(2022 개정 교육과정)으로 자동 채워지고, 데이터가 없는 학년·과목은 예시 칩으로 폴백한다.
 * 생기부 교과세특은 과목 칩을 카탈로그 과목 목록으로 대체하고 성취기준(최대 3개)을 보조 근거로 붙인다(관찰 메모는 유지).
 */
import { MAX_EXAM_STANDARDS, MAX_RECORD_STANDARDS, toAchievementStandardsPayload } from './hwpStandards';

export const HWP_DOC_CAMPAIGN = 'hwp_doc_2026q3';
export const HWP_DOC_TYPES = Object.freeze(['exam', 'notice', 'record']);
export const HWP_DOC_DEFAULT_TYPE = 'exam';
export const HWP_DOC_MAX_TEXT_CHARS = 400;
export const HWP_DOC_RESULT_STORAGE_KEY = 'whatquiz_hwp_doc_event_result';
export const HWP_DOC_RESULT_TTL_MS = 24 * 60 * 60 * 1000;

const GRADE_OPTIONS = Object.freeze([
  { id: 'e3', label: '초3' },
  { id: 'e4', label: '초4' },
  { id: 'e5', label: '초5' },
  { id: 'e6', label: '초6' },
  { id: 'm1', label: '중1' },
  { id: 'm2', label: '중2' },
  { id: 'm3', label: '중3' },
  { id: 'h1', label: '고1' },
  { id: 'h2', label: '고2' },
  { id: 'h3', label: '고3' },
]);

const SCHOOL_LEVEL_OPTIONS = Object.freeze([
  { id: 'elementary', label: '초등' },
  { id: 'middle', label: '중등' },
  { id: 'high', label: '고등' },
]);

const EXAM_SUBJECT_OPTIONS = Object.freeze([
  { id: 'korean', label: '국어' },
  { id: 'math', label: '수학' },
  { id: 'social', label: '사회' },
  { id: 'science', label: '과학' },
  { id: 'english', label: '영어' },
]);

/**
 * 과목별 단원/주제 예시 칩. 첫 칩이 기본값이다.
 * 성취기준 카탈로그를 불러오기 전(첫 렌더)과 데이터가 없는 학년·과목의 폴백 입력에서만 쓴다.
 */
export const HWP_EXAM_TOPIC_EXAMPLES = Object.freeze({
  korean: ['비유하는 표현 찾기', '주장과 근거 파악하기', '문장의 호응 관계', '설명하는 글 요약하기'],
  math: ['분수의 덧셈과 뺄셈', '약수와 배수', '소수의 곱셈', '직육면체의 부피', '일차방정식 풀기'],
  social: ['우리나라의 기후와 생활', '민주주의와 선거', '지도 읽기와 축척', '조선의 건국과 발전'],
  science: ['식물의 구조와 기능', '물의 상태 변화', '태양계와 행성', '전기 회로 만들기', '광합성'],
  english: ['현재진행형 문장 만들기', '일상 대화 표현 익히기', '동물 이름과 특징', '길 묻고 답하기'],
});

const NOTICE_KIND_OPTIONS = Object.freeze([
  { id: 'general', label: '가정통신문' },
  { id: 'field_trip', label: '현장학습 안내' },
  { id: 'counseling', label: '학부모 상담 안내' },
  { id: 'after_school', label: '방과후 프로그램 안내' },
  { id: 'school_event', label: '학교 행사 안내' },
]);

/** 안내문 종류별 핵심 내용 예시 칩. 첫 칩이 기본값이다. */
export const HWP_NOTICE_KEY_POINT_EXAMPLES = Object.freeze({
  general: [
    '2학기 학사 일정과 준비물 안내, 등하교 안전 협조 요청',
    '독감 예방과 결석 시 연락 방법 안내',
    '학교 급식 만족도 조사 참여 안내',
  ],
  field_trip: [
    '10월 셋째 주 현장학습, 집합 시간·복장·준비물·참가 동의서 회신',
    '체험학습 장소와 이동 방법, 점심 도시락 안내, 우천 시 대체 일정',
    '참가비 납부 방법과 안전 유의사항',
  ],
  counseling: [
    '학기 초 학부모 상담 주간 운영, 대면·전화 상담 선택, 신청서 회신',
    '상담 가능 시간표 안내와 온라인 신청 방법',
    '학생 생활·학습 상담 주제 예시와 준비 사항',
  ],
  after_school: [
    '2학기 방과후 프로그램 개설 강좌, 신청 기간, 수강료 안내',
    '프로그램별 요일·시간표와 정원, 선착순 접수 방법',
    '수강 취소·환불 규정과 문의 연락처',
  ],
  school_event: [
    '학예 발표회 일시·장소·프로그램 순서, 학부모 참관 안내',
    '운동회 일정과 종목, 학생 준비물, 학부모 참여 종목 안내',
    '학교 축제 부스 운영과 참관 시 주의 사항',
  ],
});

const RECORD_AREA_OPTIONS = Object.freeze([
  { id: 'subject', label: '교과세특' },
  { id: 'behavior', label: '행동특성' },
  { id: 'autonomy', label: '창체 자율' },
  { id: 'club', label: '창체 동아리' },
  { id: 'career', label: '창체 진로' },
]);

/** 영역별 과목/동아리 선택 칩. 첫 칩이 기본값이다. */
export const HWP_RECORD_SUBJECT_OPTIONS = Object.freeze({
  subject: ['국어', '수학', '사회', '과학', '영어'],
  behavior: ['학급 생활', '교우 관계', '자기 관리'],
  autonomy: ['학급 자치', '학교 행사', '안전·환경 활동'],
  club: ['과학 탐구반', '독서 토론반', '방송·영상반', '체육 동아리'],
  career: ['진로 탐색 활동', '직업 체험', '진로 독서'],
});

/** 영역별 관찰 메모 예시 칩(실명·학번 없음). 첫 칩이 기본값이다. */
export const HWP_RECORD_OBSERVATION_EXAMPLES = Object.freeze({
  subject: [
    '모둠 활동에서 자료를 나눠 정리하고 발표 순서를 조율함. 친구 질문에 예시를 들어 설명함. 수업 개념을 생활 사례에 연결해 정리함.',
    '문제 풀이 과정을 그림으로 표현해 설명함. 오답을 스스로 점검하고 다시 풀어봄. 어려운 문항에도 끝까지 도전함.',
    '토론에서 근거 자료를 찾아 주장을 뒷받침함. 상대 의견을 경청하고 반론을 정리함. 글쓰기 과제에서 문장을 다듬는 습관을 보임.',
  ],
  behavior: [
    '학급 청소 시간에 맡은 구역을 꾸준히 정리함. 친구가 힘들어할 때 먼저 도움을 줌. 규칙을 지키려 노력하고 약속을 잘 지킴.',
    '모둠에서 의견이 갈릴 때 중재 역할을 함. 새 친구가 적응하도록 안내함. 실수했을 때 솔직하게 인정하고 고침.',
  ],
  autonomy: [
    '학급 회의에서 안건을 정리해 제안함. 학급 행사 준비에서 역할을 나누고 진행을 도움. 결과를 정리해 학급 게시판에 공유함.',
    '안전 교육 후 교실 위험 요소를 점검해 발표함. 환경 정화 활동에 적극 참여함.',
  ],
  club: [
    '동아리 실험 활동에서 변인을 정리하고 기록을 맡음. 결과를 그래프로 정리해 발표함. 후배 부원에게 실험 방법을 안내함.',
    '독서 토론에서 책의 쟁점을 정리해 발제함. 다른 의견을 존중하며 토론을 이끌어감.',
  ],
  career: [
    '직업 체험에서 관심 분야를 정해 질문 목록을 준비함. 체험 후 느낀 점을 정리해 발표함. 관련 도서를 찾아 읽고 후속 탐구를 계획함.',
    '진로 검사 결과를 바탕으로 강점을 정리함. 관심 직업의 필요 역량을 조사해 공유함.',
  ],
});

const COUNT_OPTIONS = Object.freeze([
  { id: 5, label: '5문항' },
  { id: 10, label: '10문항' },
  { id: 15, label: '15문항' },
]);

const DIFFICULTY_OPTIONS = Object.freeze([
  { id: 'easy', label: '쉬움' },
  { id: 'normal', label: '보통' },
  { id: 'hard', label: '어려움' },
]);

const DATE_HINT_OPTIONS = Object.freeze([
  { id: '', label: '날짜 없이' },
  { id: '이번 주', label: '이번 주' },
  { id: '다음 주', label: '다음 주' },
  { id: '이번 달 말', label: '이번 달 말' },
]);

/**
 * 유형별 설정. fields는 화면 순서대로 렌더링한다.
 * - kind 'chips': options 중 하나 선택 (defaultValue 필수)
 * - kind 'text-chips': examplesByKey[상위 필드 값] 예시 칩 + 입력창. 상위 필드가 바뀌면 첫 예시로 갱신.
 * - kind 'text-optional': 접힌 선택 입력. 기본값에 없고 비어 있으면 서버에 보내지 않는다.
 *
 * 성취기준 선택기(HwpStandardsPicker) 연결 — `standardsPicker` 가 붙은 필드 하나가 선택기를 그린다:
 *  - { mode: 'topic' }  (시험지 text-chips): 성취기준 칩이 이 필드를 자동으로 채우고, 예시 칩은 폴백.
 *  - { mode: 'subject', when: { area: 'subject' } } (생기부 dependent-chips): 조건이 맞을 때 이 칩 행을
 *    카탈로그 과목 칩 + 성취기준 칩으로 대체한다. 조건이 안 맞거나 데이터가 없으면 기존 칩 그대로.
 *  `standardsScopeKeys` 의 칩이 바뀌면 고른 성취기준을 비운다(선택기가 새 범위의 첫 항목을 다시 자동 선택).
 *  `standardsMax` 는 서버 페이로드 상한(시험지 5·생기부 3).
 */
export const HWP_DOC_EVENT_CONFIG = Object.freeze({
  exam: {
    id: 'exam',
    path: '/events/hwp-exam',
    lpPath: '/lp/hwp-exam',
    navLabel: '시험지',
    pageTitle: '단원만 고르면 시험지가 한글 파일로 | 왓퀴즈',
    headline: '단원만 고르면\n시험지가 한글 파일로',
    subheadline: '학년·과목·성취기준을 누르면 교육과정에 맞는 문항과 정답이 들어간 시험지가 만들어져요.',
    heroExample: {
      src: '/images/events/hwp-exam-example.webp',
      alt: '왓퀴즈로 만든 중학교 2학년 과학 단원 평가를 한글 편집기로 연 화면',
    },
    submitLabel: '시험지 만들기',
    resultReadyLabel: '시험지 한글 파일이 준비됐어요',
    shareText: '단원만 골랐는데 시험지가 한글 파일로 나왔어요.',
    loadingSteps: ['문항 구성 중', '서식 맞추는 중', '한글 파일로 저장 중'],
    // 결과 화면 이어가기 CTA: /chat AI 한글에 방금 만든 HWPX 를 첨부한 채 시작한다.
    continueCta: {
      destination: 'chat',
      chatCategory: 'document',
      title: '이 시험지, 이어서 편집하기',
      description: '3번을 서술형으로, 난이도 올리기 같은 수정이 대화로 바로 돼요.',
      buttonLabel: '왓퀴즈에서 이어서 편집',
      editSuggestions: ['3번 문항을 서술형으로 바꿔줘', '난이도를 한 단계 올려줘', '정답·해설을 마지막 페이지로 옮겨줘'],
    },
    standardsScopeKeys: ['grade', 'subject'],
    standardsMax: MAX_EXAM_STANDARDS,
    fields: [
      { key: 'grade', label: '학년', kind: 'chips', options: GRADE_OPTIONS, defaultValue: 'e5' },
      { key: 'subject', label: '과목', kind: 'chips', options: EXAM_SUBJECT_OPTIONS, defaultValue: 'math' },
      {
        key: 'topic',
        label: '단원 / 주제',
        kind: 'text-chips',
        dependsOn: 'subject',
        examplesByKey: HWP_EXAM_TOPIC_EXAMPLES,
        placeholder: '예: 분수의 덧셈과 뺄셈',
        maxLength: 120,
        // 성취기준 칩이 topic 을 자동으로 채운다. 카탈로그에 없는 학년·과목은 위 예시 칩으로 폴백.
        standardsPicker: { mode: 'topic' },
      },
      { key: 'question_count', label: '문항 수', kind: 'chips', options: COUNT_OPTIONS, defaultValue: 10 },
      { key: 'difficulty', label: '난이도', kind: 'chips', options: DIFFICULTY_OPTIONS, defaultValue: 'normal' },
      {
        key: 'extra',
        label: '추가 요청',
        kind: 'text-optional',
        placeholder: '예: 서술형은 실생활 문제로, 3번은 그림 자료 활용',
        maxLength: 300,
      },
    ],
  },
  notice: {
    id: 'notice',
    path: '/events/hwp-notice',
    lpPath: '/lp/hwp-notice',
    navLabel: '안내문',
    pageTitle: '가정통신문, 클릭 몇 번이면 한글 파일로 | 왓퀴즈',
    headline: '가정통신문,\n클릭 몇 번이면 한글 파일로',
    subheadline: '안내문 종류와 핵심 내용만 고르면 학부모용 안내문이 완성돼요.',
    heroExample: {
      src: '/images/events/hwp-notice-example.webp',
      alt: '왓퀴즈로 만든 3학년 현장체험학습 안내 가정통신문을 한글 편집기로 연 화면',
    },
    submitLabel: '안내문 만들기',
    resultReadyLabel: '안내문 한글 파일이 준비됐어요',
    shareText: '클릭 몇 번으로 가정통신문이 한글 파일로 나왔어요.',
    loadingSteps: ['안내 내용 정리 중', '서식 맞추는 중', '한글 파일로 저장 중'],
    continueCta: {
      destination: 'chat',
      chatCategory: 'document',
      title: '이 안내문, 이어서 다듬기',
      description: '날짜·문구 수정, 학교 양식 맞추기가 대화로 바로 돼요.',
      buttonLabel: '왓퀴즈에서 이어서 다듬기',
      editSuggestions: ['날짜를 다음 주 금요일로 바꿔줘', '문장을 더 정중하게 다듬어줘', '회신서 양식을 추가해줘'],
    },
    fields: [
      { key: 'notice_kind', label: '안내문 종류', kind: 'chips', options: NOTICE_KIND_OPTIONS, defaultValue: 'general' },
      { key: 'school_level', label: '학교급', kind: 'chips', options: SCHOOL_LEVEL_OPTIONS, defaultValue: 'elementary' },
      {
        key: 'key_points',
        label: '핵심 내용',
        kind: 'text-chips',
        dependsOn: 'notice_kind',
        examplesByKey: HWP_NOTICE_KEY_POINT_EXAMPLES,
        placeholder: '예: 일정, 준비물, 회신 방법',
        maxLength: HWP_DOC_MAX_TEXT_CHARS,
        multiline: true,
      },
      { key: 'date_hint', label: '날짜 (선택)', kind: 'chips', options: DATE_HINT_OPTIONS, defaultValue: '' },
    ],
  },
  record: {
    id: 'record',
    path: '/events/hwp-record',
    lpPath: '/lp/hwp-record',
    navLabel: '생기부',
    // 생기부 문장은 한글 파일이 아니라 텍스트 결과(문장 카드 + 복사)로 바로 보여준다.
    resultKind: 'text',
    eventLabel: '생기부 문장 이벤트',
    shareTitlePrefix: '[생기부 문장]',
    pageTitle: '관찰 메모만 적으면 세특 문장이 바로 | 왓퀴즈',
    headline: '관찰 메모만 적으면\n세특 문장이 바로',
    subheadline: '영역·과목·성취기준을 누르고 관찰 메모를 적으면 생기부 문장 초안이 여러 안으로 나와요.',
    submitLabel: '세특 문장 만들기',
    resultReadyLabel: '생기부 문장 초안이 준비됐어요',
    sharedEntryLabel: '공유된 생기부 문장',
    shareText: '관찰 메모만 적었는데 세특 문장이 여러 안으로 나왔어요.',
    loadingSteps: ['관찰 메모 읽는 중', '문장 다듬는 중', '안별로 정리하는 중'],
    // 생기부는 /chat 이 아니라 AI 생기부 생성(/create-saenggibu)으로만 이어간다. chatCategory 없음.
    continueCta: {
      destination: 'saenggibu',
      title: 'AI 생기부 생성에서 이어서 쓰기',
      description: '학생 여러 명을 한 번에, 교과세특·행발·창체까지.',
      buttonLabel: 'AI 생기부 생성에서 이어서 쓰기',
    },
    privacyNotice: '실명·학번은 적지 마세요. 관찰한 행동만 적으면 돼요.',
    // 교과세특에서만 성취기준을 붙인다(행발·창체는 선택기 없음). 영역·학교급·과목이 바뀌면 선택을 비운다.
    standardsScopeKeys: ['area', 'school_level', 'subject_or_club'],
    standardsMax: MAX_RECORD_STANDARDS,
    standardsArea: 'subject',
    fields: [
      { key: 'area', label: '영역', kind: 'chips', options: RECORD_AREA_OPTIONS, defaultValue: 'subject' },
      { key: 'school_level', label: '학교급', kind: 'chips', options: SCHOOL_LEVEL_OPTIONS, defaultValue: 'middle' },
      {
        key: 'subject_or_club',
        label: '과목 / 활동',
        kind: 'dependent-chips',
        dependsOn: 'area',
        optionsByKey: HWP_RECORD_SUBJECT_OPTIONS,
        // 교과세특: 이 칩 행을 카탈로그 과목 칩 + 성취기준 칩으로 대체한다. 데이터가 없으면 위 옵션 그대로.
        standardsPicker: { mode: 'subject', when: { area: 'subject' } },
      },
      {
        key: 'observations',
        label: '관찰 메모',
        kind: 'text-chips',
        dependsOn: 'area',
        examplesByKey: HWP_RECORD_OBSERVATION_EXAMPLES,
        placeholder: '예: 모둠 활동에서 역할을 나눠 자료를 정리함.',
        maxLength: HWP_DOC_MAX_TEXT_CHARS,
        multiline: true,
      },
    ],
  },
});

/**
 * 결과 화면 하단 교차 카드. 현재 유형을 제외한 한글 이벤트 페이지만 연결한다.
 * 학습지·놀이 PPT·이미지 이벤트는 넣지 않는다. notice 카드는 가정통신문을 명시한다.
 */
const HWP_DOC_RELATED_CARDS = Object.freeze({
  exam: {
    type: 'exam',
    path: '/events/hwp-exam',
    title: '시험지도 한글 파일로',
    description: '학년·과목만 고르면 단원평가가 바로 나와요',
  },
  notice: {
    type: 'notice',
    path: '/events/hwp-notice',
    title: '가정통신문도 한글 파일로',
    description: '학부모 안내문을 클릭 몇 번으로 만들어요',
  },
  record: {
    type: 'record',
    path: '/events/hwp-record',
    title: '생기부 문장도 바로',
    description: '관찰 메모만 적으면 세특 초안이 여러 안으로',
  },
});

export const HWP_DOC_CROSS_LINKS = Object.freeze(
  Object.fromEntries(
    HWP_DOC_TYPES.map((type) => [
      type,
      HWP_DOC_TYPES.filter((other) => other !== type).map((other) => HWP_DOC_RELATED_CARDS[other]),
    ]),
  ),
);

export const isHwpDocType = (value) => HWP_DOC_TYPES.includes(value);

export const getHwpDocCrossLinks = (type) => (
  HWP_DOC_CROSS_LINKS[isHwpDocType(type) ? type : HWP_DOC_DEFAULT_TYPE]
);

export const getHwpDocConfig = (type) => (
  HWP_DOC_EVENT_CONFIG[isHwpDocType(type) ? type : HWP_DOC_DEFAULT_TYPE]
);

/** 결과 형태. exam/notice는 'hwpx'(rhwp 미리보기·다운로드), record는 'text'(문장 카드·복사). */
export const getHwpDocResultKind = (type) => getHwpDocConfig(type).resultKind || 'hwpx';
export const isHwpDocTextResult = (type) => getHwpDocResultKind(type) === 'text';

/** 상위 필드 값에 따른 예시/옵션 목록. */
export const getHwpDocDependentList = (field, parentValue) => {
  const source = field.kind === 'dependent-chips' ? field.optionsByKey : field.examplesByKey;
  if (!source) return [];
  return source[parentValue] || Object.values(source)[0] || [];
};

/** 유형별 기본 fields. 클릭 0번으로 "만들기"가 가능한 값을 채운다. */
export const buildHwpDocDefaultFields = (type) => {
  const config = getHwpDocConfig(type);
  const fields = {};
  config.fields.forEach((field) => {
    if (field.kind === 'chips') {
      fields[field.key] = field.defaultValue;
    }
  });
  config.fields.forEach((field) => {
    if (field.kind === 'text-chips' || field.kind === 'dependent-chips') {
      const list = getHwpDocDependentList(field, fields[field.dependsOn]);
      fields[field.key] = list[0] || '';
    }
  });
  return fields;
};

/** 칩 표시용 라벨 (grade/subject 등 id → 한국어 라벨). */
export const getHwpDocOptionLabel = (type, key, value) => {
  const field = getHwpDocConfig(type).fields.find((item) => item.key === key);
  if (!field?.options) return String(value ?? '');
  const option = field.options.find((item) => item.id === value);
  return option ? option.label : String(value ?? '');
};

/**
 * 서버 전송용 fields 정규화. 텍스트는 공백 정리 + 길이 제한, 칩 값은 허용 목록만 통과.
 * 허용되지 않은 값은 기본값으로 되돌린다.
 */
export const normalizeHwpDocFields = (type, rawFields = {}) => {
  const config = getHwpDocConfig(type);
  const defaults = buildHwpDocDefaultFields(type);
  const result = {};
  config.fields.forEach((field) => {
    const raw = rawFields[field.key];
    if (field.kind === 'chips') {
      result[field.key] = field.options.some((item) => item.id === raw) ? raw : defaults[field.key];
      return;
    }
    const text = String(raw ?? '').replace(/\s+/g, ' ').trim();
    const maxLength = field.maxLength || HWP_DOC_MAX_TEXT_CHARS;
    // 선택 입력은 비어 있으면 키 자체를 보내지 않는다(구 클라이언트와 같은 페이로드).
    if (field.kind === 'text-optional' && !text) return;
    result[field.key] = text.slice(0, maxLength);
  });
  // 성취기준: 고른 것이 있을 때만 [{key, code, text}] 로 보낸다(시험지 5개·생기부 교과세특 3개).
  const standards = getHwpDocStandardsPayload(type, { ...result, achievement_standards: rawFields.achievement_standards });
  if (standards.length > 0) result.achievement_standards = standards;
  return result;
};

/**
 * 서버로 보낼 성취기준 페이로드. 선택기가 붙는 유형에서, 생기부는 교과세특(area=subject)일 때만.
 * `scopeFields` 는 정규화 전/후 어느 fields 든 area 판정에 쓸 수 있다.
 */
export const getHwpDocStandardsPayload = (type, scopeFields = {}) => {
  const config = getHwpDocConfig(type);
  if (!config.standardsMax || !Array.isArray(scopeFields.achievement_standards)) return [];
  if (config.standardsArea && scopeFields.area !== config.standardsArea) return [];
  return toAchievementStandardsPayload(scopeFields.achievement_standards, config.standardsMax);
};

/**
 * 필수 텍스트 필드가 비었는지 확인 (subject_or_club, date_hint는 선택).
 * 생기부 교과세특은 성취기준을 골랐으면 관찰 메모 없이도 만들 수 있다(성취기준이 보조 근거가 된다).
 */
export const getHwpDocMissingField = (type, fields = {}) => {
  const config = getHwpDocConfig(type);
  const required = config.fields.find((field) => field.kind === 'text-chips');
  if (!required) return null;
  const text = String(fields[required.key] || '').trim();
  if (text) return null;
  if (config.id === 'record' && getHwpDocStandardsPayload(type, fields).length > 0) return null;
  return required;
};

/** /create-saenggibu 카테고리. 고등 교과세특만 교과_고등. */
export const getHwpRecordSaenggibuCategory = (area, schoolLevel) => {
  const level = schoolLevel === 'high' || schoolLevel === '고등' ? '고등' : schoolLevel;
  if (area === 'subject' || area === '교과세특') return level === '고등' ? '교과_고등' : '교과';
  if (area === 'club' || area === '창체 동아리') return '동아리';
  if (area === 'career' || area === '창체 진로') return '진로';
  if (area === 'autonomy' || area === '창체 자율') return '창체';
  return '행동발달';
};

/**
 * AI 생기부 생성 바이트 가이드와 같은 영역 상한.
 * 초등 창체 자율·동아리 3,000 / 진로 2,100 / 나머지 1,500.
 */
export const getHwpRecordMaxBytes = (area, schoolLevel) => {
  const category = getHwpRecordSaenggibuCategory(area, schoolLevel);
  const level = schoolLevel === 'elementary' || schoolLevel === '초등' ? '초등' : schoolLevel;
  if (category === '진로') return 2100;
  if ((category === '창체' || category === '동아리') && level === '초등') return 3000;
  return 1500;
};

export const HWP_DOC_TYPE_BY_PATH = Object.freeze(
  Object.values(HWP_DOC_EVENT_CONFIG).reduce((acc, config) => {
    acc[config.path] = config.id;
    acc[config.lpPath] = config.id;
    return acc;
  }, {}),
);
